import { checkColor, rad, withColor, type PieceKind, type Placed, type PrimKind } from "./types";

/**
 * A stair set (#10): `steps` steps, each `rise` tall and `run` deep, going
 * down toward +(cos rot, sin rot), ±hw across. The top step is
 * `steps * rise` tall, level with the platform it usually comes off. Each
 * step is taller than the sim steps up (`STEP_UP`), so you can't roll up
 * them: roll or ollie down.
 */
export interface StairsPiece {
  kind: "stairs";
  /** Centre of the whole run of steps. */
  x: number;
  z: number;
  rot?: number;
  hw: number;
  steps: number;
  rise: number;
  run: number;
  /** Its own color (#115), over the map's `look.pieces`. */
  color?: string;
}

/** A stair set as one primitive (#115): step i from the top is `rise * (steps - i)` tall, centred on (x, z). */
export interface StairsPrim extends Placed {
  kind: "stairs";
  steps: number;
  rise: number;
  run: number;
  /** Render only (#115): its own color. */
  color?: string;
}

/** The lowest step height allowed (m): past `STEP_UP` (sim/tuning.ts), what rolling steps up. */
export const STAIR_MIN_RISE = 0.3;

/** Which step (0 the top) covers local u; on the line between two, the higher one. */
export function stepAt(p: StairsPrim, u: number): number {
  return Math.max(0, Math.ceil((u + (p.steps * p.run) / 2) / p.run) - 1);
}

export const STAIRS: PrimKind<StairsPrim> = {
  eval(p, u, w) {
    const half = (p.steps * p.run) / 2;
    if (Math.abs(u) > half || Math.abs(w) > p.hw) return null;
    return { h: p.rise * (p.steps - Math.min(p.steps - 1, stepAt(p, u))), du: 0, dw: 0 };
  },
  extent: (p) => [(-p.steps * p.run) / 2, (p.steps * p.run) / 2],
  shadow: 0.55,
  top: (p) => p.rise * p.steps,
  plan: (p) => [{ u0: (-p.steps * p.run) / 2, u1: (p.steps * p.run) / 2, fill: "box" }],
};

export function stairs(p: StairsPiece): StairsPrim {
  return withColor<StairsPrim>({ kind: "stairs", x: p.x, z: p.z, rot: rad(p.rot), hw: p.hw, steps: p.steps, rise: p.rise, run: p.run }, p.color);
}

export const STAIRS_PIECE: PieceKind<StairsPiece> = {
  fields: ["x", "z", "rot", "hw", "steps", "rise", "run", "color"],
  placed: true,
  check(c, o, path) {
    const n = c.num(o, "steps", path, { min: 2, max: 20 });
    if (!Number.isInteger(n)) c.fail(`${path}.steps`, "expected a whole number");
    c.num(o, "rise", path, { min: STAIR_MIN_RISE, max: 0.6 });
    c.num(o, "run", path, { min: 0.3, max: 2 });
    checkColor(c, o, path);
  },
  compile(p, out) {
    out.prims.push(stairs(p));
  },
};
